import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';
import { useAuthStore } from './authStore';
import { markOwnWrite } from '../lib/realtime/ownWrites';

export interface StockNotification {
  id: string;
  requestId: string;
  lineId: string | null;
  jobId: string | null;
  siteId: string | null;
  type: string;
  message: string;
  read: boolean;
  createdAt: Date;
}

interface NotificationsState {
  notifications: StockNotification[];
  unreadCount: number;
  isLoading: boolean;

  fetchNotifications: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Called by store actions to insert a notification for a user */
  notify: (
    recipientId: string,
    requestId: string,
    type: string,
    message: string,
    lineId?: string | null,
    jobId?: string | null,
    siteId?: string | null,
  ) => Promise<void>;
  applyRemoteRow: (event: 'INSERT' | 'UPDATE' | 'DELETE', row: Record<string, unknown> | null) => void;
}

const toNotif = (row: Record<string, unknown>): StockNotification => ({
  id: row.id as string,
  requestId: row.request_id as string,
  lineId: (row.line_id as string | null) ?? null,
  jobId: (row.job_id as string | null) ?? null,
  siteId: (row.site_id as string | null) ?? null,
  type: row.type as string,
  message: row.message as string,
  read: row.read as boolean,
  createdAt: new Date(row.created_at as string),
});

export function isJobStockNotification(type: string): boolean {
  return type === 'job_stock_ready' || type === 'job_stock_partial';
}

export const useStockNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,

  fetchNotifications: async () => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    set({ isLoading: true });
    const { data, error } = await supabase
      .from('stock_request_notifications')
      .select('*')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);

    if (error || !data) { set({ isLoading: false }); return; }

    const notifications = data.map(r => toNotif(r as Record<string, unknown>));
    set({
      notifications,
      unreadCount: notifications.filter(n => !n.read).length,
      isLoading: false,
    });
  },

  markRead: async (id: string) => {
    await supabase
      .from('stock_request_notifications')
      .update({ read: true })
      .eq('id', id);
    set(s => {
      const notifications = s.notifications.map(n => n.id === id ? { ...n, read: true } : n);
      return { notifications, unreadCount: notifications.filter(n => !n.read).length };
    });
  },

  markAllRead: async () => {
    const user = useAuthStore.getState().user;
    if (!user) return;
    await supabase
      .from('stock_request_notifications')
      .update({ read: true })
      .eq('recipient_id', user.id)
      .eq('read', false);
    set(s => ({
      notifications: s.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  notify: async (recipientId, requestId, type, message, lineId = null, jobId = null, siteId = null) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return;

    const { data } = await supabase
      .from('stock_request_notifications')
      .insert({
        collection_id: collectionId,
        recipient_id: recipientId,
        request_id: requestId,
        line_id: lineId,
        job_id: jobId,
        site_id: siteId,
        type,
        message,
      })
      .select()
      .single();

    if (!data) return;
    markOwnWrite('stock_request_notifications', data.id as string);

    const user = useAuthStore.getState().user;
    if (user?.id === recipientId) {
      const notif = toNotif(data as Record<string, unknown>);
      set(s => ({
        notifications: [notif, ...s.notifications],
        unreadCount: s.unreadCount + 1,
      }));
    }
  },

  applyRemoteRow: (event, row) => {
    const id = row?.id as string | undefined;
    if (!id) return;
    if (event === 'DELETE') {
      set((s) => {
        const notifications = s.notifications.filter((n) => n.id !== id);
        return { notifications, unreadCount: notifications.filter((n) => !n.read).length };
      });
      return;
    }
    const notif = toNotif(row as Record<string, unknown>);
    set((s) => {
      const exists = s.notifications.some((n) => n.id === notif.id);
      const notifications = exists
        ? s.notifications.map((n) => (n.id === notif.id ? notif : n))
        : [notif, ...s.notifications];
      return { notifications, unreadCount: notifications.filter((n) => !n.read).length };
    });
  },
}));
